/**
 * Spectral Signature Classification
 *
 * Different physical objects reflect sound differently across frequency.
 * This module produces a per-band feature vector from a single ping by
 * reading already-bandpassed cross-correlations (one per band, supplied via
 * {@link BandedCorrelation}), turning each into a range profile, and reading
 * the amplitude at the already-detected peak range bin. The resulting
 * L2-normalized vector is a frequency-domain "fingerprint" suitable for
 * downstream classification.
 *
 * Inputs come from `computeBandCorrelations` so the (expensive) bandpass +
 * FFT cross-correlation work is shared across consumers.
 */

import type { BandedCorrelation } from './band-correlations.js';
import { buildRangeProfileFromCorrelation } from '../dsp/profile.js';

export interface SpectralBand {
  /** Band identifier (e.g. 'L', 'M', 'H') */
  id: string;
  /** Lower cutoff (Hz) */
  fLow: number;
  /** Upper cutoff (Hz) */
  fHigh: number;
  /** Raw amplitude in this band's range profile at bestBin (not normalized) */
  amplitude: number;
  /** Linear ratio: peak amplitude / max(median(profile), epsilon) */
  snr: number;
}

export interface SpectralSignature {
  /** One entry per band probed (typically 3: L, M, H) */
  bands: SpectralBand[];
  /** L2-normalized amplitudes [a_L, a_M, a_H], length = bands.length */
  featureVector: Float32Array;
  /** Band id with highest raw amplitude */
  dominantBandId: string;
  /** Sum of squared raw amplitudes (pre-normalization) */
  totalEnergy: number;
}

export interface ComputeSpectralSignatureArgs {
  /** Pre-computed per-band bandpass correlations (from `computeBandCorrelations`). */
  bandCorrelations: BandedCorrelation[];
  /** Detected peak bin index (in range profile) */
  bestBin: number;
  /**
   * Direct-path delay (seconds) used when the wideband range profile that
   * produced `bestBin` was built. The band correlations share the wideband
   * lag axis (mic and ref are filtered with the same FIR, so group delays
   * cancel), so the same tau0 must be applied here — otherwise `bestBin`
   * addresses a different physical range in the band profiles.
   */
  tau0: number;
  /** Sample rate (Hz) */
  sampleRate: number;
  /** Range profile min (m) */
  minRange: number;
  /** Range profile max (m) */
  maxRange: number;
  /** Number of range bins */
  heatBins: number;
  /** m/s (default 343) */
  speedOfSound?: number;
}

/** Floor used to avoid divide-by-zero in SNR computation. */
const SNR_EPSILON = 1e-12;

function median(values: Float32Array): number {
  if (values.length === 0) return 0;
  const sorted = Float32Array.from(values).sort();
  const mid = sorted.length >> 1;
  if (sorted.length % 2 === 1) return sorted[mid];
  return 0.5 * (sorted[mid - 1] + sorted[mid]);
}

/**
 * Compute a per-band spectral signature for an already-detected echo.
 *
 * For each entry in `bandCorrelations`:
 *   1. Build a range profile from the entry's real correlation over
 *      [minRange, maxRange] using the standard profile.ts mapping.
 *   2. Read amplitude at bestBin.
 *   3. Compute SNR = amplitude / max(median(profile), eps).
 *
 * Then assemble totalEnergy, L2-normalized featureVector, and dominantBandId.
 */
export function computeSpectralSignature(
  args: ComputeSpectralSignatureArgs,
): SpectralSignature {
  const {
    bandCorrelations,
    bestBin,
    tau0,
    sampleRate,
    minRange,
    maxRange,
    heatBins,
    speedOfSound = 343,
  } = args;

  const bandResults: SpectralBand[] = [];
  const amplitudes = new Float32Array(bandCorrelations.length);

  const safeBin = bestBin >= 0 && bestBin < heatBins ? bestBin : -1;
  const inputsValid =
    sampleRate > 0 &&
    heatBins > 0 &&
    maxRange > minRange;

  for (let i = 0; i < bandCorrelations.length; i++) {
    const entry = bandCorrelations[i];
    let amplitude = 0;
    let snr = 0;

    if (inputsValid && entry.corrReal.length > 0) {
      const profile = buildRangeProfileFromCorrelation(
        entry.corrReal,
        tau0,
        speedOfSound,
        minRange,
        maxRange,
        sampleRate,
        heatBins,
        false,
      );

      if (safeBin >= 0 && safeBin < profile.length) {
        amplitude = profile[safeBin];
      }
      const med = median(profile);
      snr = amplitude / Math.max(med, SNR_EPSILON);
    }

    amplitudes[i] = amplitude;
    bandResults.push({
      id: entry.id,
      fLow: entry.fLow,
      fHigh: entry.fHigh,
      amplitude,
      snr,
    });
  }

  // Total energy = sum of squared raw amplitudes
  let totalEnergy = 0;
  for (let i = 0; i < amplitudes.length; i++) {
    totalEnergy += amplitudes[i] * amplitudes[i];
  }

  // L2-normalize. If totalEnergy is 0, return zero vector of correct length.
  const featureVector = new Float32Array(amplitudes.length);
  if (totalEnergy > 0) {
    const norm = Math.sqrt(totalEnergy);
    for (let i = 0; i < amplitudes.length; i++) {
      featureVector[i] = amplitudes[i] / norm;
    }
  }

  // Dominant band = max raw amplitude. Fall back to first band id if all zero.
  let dominantIdx = 0;
  let dominantVal = -Infinity;
  for (let i = 0; i < amplitudes.length; i++) {
    if (amplitudes[i] > dominantVal) {
      dominantVal = amplitudes[i];
      dominantIdx = i;
    }
  }
  const dominantBandId = bandResults.length > 0
    ? bandResults[dominantIdx].id
    : '';

  return {
    bands: bandResults,
    featureVector,
    dominantBandId,
    totalEnergy,
  };
}
